// Tenantra Inc. - Official Rent Report Generator & PDF View
import { useState } from 'react';
import { 
  FileText, Download, Printer, CheckCircle, 
  Shield, QrCode, Calendar, Home, DollarSign, AlertTriangle,
  Clock, XCircle, ArrowLeft, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LogoMark } from '@/components/Logo';

// Score rating based on 0-100 scale
const getScoreRating = (score) => {
  if (score === null) return { label: 'NEW TENANT', color: 'text-slate-500' };
  if (score >= 85) return { label: 'EXCELLENT', color: 'text-emerald-600' };
  if (score >= 70) return { label: 'GOOD', color: 'text-teal-600' };
  if (score >= 50) return { label: 'FAIR', color: 'text-amber-600' };
  return { label: 'POOR', color: 'text-red-600' };
};

// Selection Screen Component
export function ReportSelectionScreen({ tenancies, onGenerate, onCancel }) {
  const [selectedCount, setSelectedCount] = useState(2);
  
  const options = [
    { value: 1, label: 'Most recent tenancy only', desc: '1 property' },
    { value: 2, label: 'Last 2 tenancies', desc: 'Recommended for most landlords' },
    { value: 3, label: 'Last 3 tenancies', desc: '3 properties' },
    { value: tenancies.length, label: 'All tenancies on file', desc: `${tenancies.length} properties` },
  ].filter((opt, idx, arr) => {
    // Remove duplicates and options > total tenancies
    if (opt.value > tenancies.length) return false;
    return arr.findIndex(o => o.value === opt.value) === idx;
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 max-w-lg mx-auto shadow-sm" data-testid="report-selection">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-slate-900 mb-1">Generate Official Report</h2>
        <p className="text-slate-500">
          Your account has {tenancies.length} verified {tenancies.length === 1 ? 'tenancy' : 'tenancies'} on file.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        <p className="text-sm font-medium text-slate-700">Choose which tenancies to include:</p>
        {options.map((opt) => (
          <label 
            key={opt.value}
            className={`flex items-center gap-3 p-4 rounded-md border cursor-pointer transition-all ${
              selectedCount === opt.value 
                ? 'border-teal-500 bg-teal-50' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              name="tenancy-count"
              value={opt.value}
              checked={selectedCount === opt.value}
              onChange={() => setSelectedCount(opt.value)}
              className="w-4 h-4 text-teal-600"
            />
            <div className="flex-1">
              <p className="text-slate-800 font-medium">{opt.label}</p>
              <p className="text-sm text-slate-500">{opt.desc}</p>
            </div>
          </label>
        ))}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-slate-600">
          <strong>Note:</strong> Your score is calculated from ALL tenancies, but only 
          the selected tenancies will appear in the printed report.
        </p>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={() => onGenerate(selectedCount)} 
          data-testid="generate-official-btn"
          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
        >
          Generate Report
        </Button>
      </div>
    </div>
  );
}

// Printable Report Component
export function OfficialRentReport({ data, selectedTenancies, onClose }) {
  const reportId = `TR-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`;
  const generatedDate = new Date().toLocaleDateString('en-CA');
  const validUntil = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA');
  const birthYear = data.date_of_birth ? new Date(data.date_of_birth).getFullYear() : 'N/A';
  const scoreRating = getScoreRating(data.score?.score);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Trigger print dialog which allows saving as PDF
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Screen Header - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={onClose} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Report
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <Printer className="w-4 h-4" />
              Print
            </Button>
            <Button onClick={handleDownload} className="gap-2 bg-teal-600 hover:bg-teal-700 text-white">
              <Download className="w-4 h-4" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Printable Report */}
      <div className="max-w-4xl mx-auto py-8 px-4 print:py-0 print:px-0 print:max-w-none">
        <div className="bg-white shadow-lg print:shadow-none" style={{ fontFamily: 'Times New Roman, serif' }}>
          
          {/* Page 1 */}
          <div className="p-8 print:p-12 relative min-h-[11in] print:min-h-0">
            
            {/* Watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] print:opacity-[0.02]">
              <span className="text-8xl font-bold text-slate-900 rotate-[-45deg] whitespace-nowrap">
                TENANTRA OFFICIAL
              </span>
            </div>

            {/* Header */}
            <div className="border-b-2 border-slate-800 pb-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <LogoMark className="w-10 h-10" color="#1e293b" />
                  <div>
                    <h1 className="text-xl font-bold text-slate-800 tracking-wide">OFFICIAL RENT REPORT</h1>
                    <p className="text-sm text-slate-600">TENANTRA INC.</p>
                  </div>
                </div>
                <div className="text-right text-sm" style={{ fontFamily: 'Courier New, monospace' }}>
                  <p className="text-slate-800">Report ID: {reportId}</p>
                  <p className="text-slate-600">Generated: {generatedDate}</p>
                  <p className="text-slate-600">Valid Until: {validUntil}</p>
                </div>
              </div>
            </div>

            {/* Section A: Tenant Identification */}
            <div className="border border-slate-300 rounded mb-6">
              <div className="bg-slate-100 px-4 py-2 border-b border-slate-300">
                <h2 className="font-bold text-slate-800 text-sm tracking-wide">SECTION A: TENANT IDENTIFICATION</h2>
              </div>
              <div className="px-4 py-3">
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                  <p><span className="text-slate-500">Name:</span> <strong className="text-slate-800">{data.tenant_name}</strong></p>
                  <p><span className="text-slate-500">Born:</span> <strong className="text-slate-800">{birthYear}</strong></p>
                  <p><span className="text-slate-500">Report Scope:</span> <strong className="text-slate-800">Last {selectedTenancies.length} {selectedTenancies.length === 1 ? 'Tenancy' : 'Tenancies'}</strong></p>
                  <p><span className="text-slate-500">Consent:</span> <strong className="text-emerald-600">Verified</strong></p>
                </div>
              </div>
            </div>

            {/* Section B: Tenant Behaviour Score */}
            <div className="border border-slate-300 rounded mb-6">
              <div className="bg-slate-100 px-4 py-2 border-b border-slate-300">
                <h2 className="font-bold text-slate-800 text-sm tracking-wide">SECTION B: TENANT BEHAVIOUR SCORE</h2>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-8">
                  {/* Score Display */}
                  <div className="text-center">
                    <div className="w-28 h-28 rounded-full border-4 border-slate-800 flex items-center justify-center mb-2">
                      <div>
                        <p className="text-4xl font-bold text-slate-800">{data.score?.score ?? '—'}</p>
                        <p className="text-xs text-slate-500">out of 100</p>
                      </div>
                    </div>
                    <p className={`font-bold ${scoreRating.color}`}>{scoreRating.label}</p>
                  </div>

                  {/* Score Bar */}
                  <div className="flex-1">
                    <div className="mb-4">
                      <div className="h-4 bg-gradient-to-r from-red-500 via-amber-500 via-teal-500 to-emerald-500 rounded-full relative">
                        <div 
                          className="absolute top-full mt-1 transform -translate-x-1/2"
                          style={{ left: `${data.score?.score || 0}%` }}
                        >
                          <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-800"></div>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-slate-500 mt-3">
                        <span>0</span>
                        <span>25</span>
                        <span>50</span>
                        <span>75</span>
                        <span>100</span>
                      </div>
                    </div>

                    {/* Score Factors */}
                    <div className="text-sm space-y-1">
                      <p className="font-semibold text-slate-700 mb-2">Score Factors:</p>
                      <p className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span>Payment History: {data.score?.payment_history?.percentage || 0}% on-time ({data.score?.payment_history?.score || 0}/{data.score?.payment_history?.max_score || 40} pts)</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span>Lease Compliance: {data.score?.lease_compliance?.percentage || 0}% fulfilled ({data.score?.lease_compliance?.score || 0}/{data.score?.lease_compliance?.max_score || 35} pts)</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span>Property Care: {data.score?.property_care?.percentage || 0}% ({data.score?.property_care?.score || 0}/{data.score?.property_care?.max_score || 25} pts)</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section C: Rental History */}
            <div className="border border-slate-300 rounded mb-6">
              <div className="bg-slate-100 px-4 py-2 border-b border-slate-300">
                <h2 className="font-bold text-slate-800 text-sm tracking-wide">SECTION C: RENTAL HISTORY</h2>
              </div>
              <div className="px-4 py-3 border-b border-slate-200 text-sm">
                <p><span className="text-slate-500">Tenancies in this report:</span> <strong>{selectedTenancies.length}</strong> (showing {selectedTenancies.length} of {data.tenancies?.length || 0} total)</p>
              </div>

              {/* Tenancy Records */}
              {selectedTenancies.map((tenancy, index) => (
                <div key={tenancy.id} className={`px-4 py-4 ${index < selectedTenancies.length - 1 ? 'border-b border-slate-200' : ''}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-slate-800 text-white text-xs font-bold px-2 py-0.5 rounded">
                      TENANCY {index + 1}
                    </span>
                    {index === 0 && <span className="text-xs text-slate-500">(Most Recent)</span>}
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <p>
                      <span className="text-slate-500 inline-block w-20">Address:</span>
                      <strong className="text-slate-800">{tenancy.property_address}</strong>
                    </p>
                    <p>
                      <span className="text-slate-500 inline-block w-20">Period:</span>
                      <strong className="text-slate-800">
                        {new Date(tenancy.move_in_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })}
                        {' – '}
                        {tenancy.move_out_date 
                          ? new Date(tenancy.move_out_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })
                          : 'Present'
                        }
                        {' '}({tenancy.duration || 'Ongoing'})
                      </strong>
                    </p>
                    <p>
                      <span className="text-slate-500 inline-block w-20">Landlord:</span>
                      <strong className="text-slate-800">Verified (ID: {tenancy.landlord_id?.slice(0, 8) || 'N/A'})</strong>
                    </p>
                    <p>
                      <span className="text-slate-500 inline-block w-20">Payment:</span>
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                        <strong className="text-slate-800">
                          {tenancy.payment_summary || 'On-time'} | Late: {tenancy.late_payments || 0} | Missed: {tenancy.missed_payments || 0} | Owed: ${tenancy.amount_owed || 0}
                        </strong>
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500 inline-block w-20">Lease:</span>
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                        <strong className="text-slate-800">
                          {tenancy.lease_status || 'Fulfilled'} | Notice: {tenancy.notice_given || 'N/A'} | Early exit: {tenancy.early_exit ? 'Yes' : 'No'}
                        </strong>
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500 inline-block w-20">Issues:</span>
                      <strong className={tenancy.issues_count > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                        {tenancy.issues_count > 0 ? `${tenancy.issues_count} reported` : 'None reported'}
                      </strong>
                    </p>
                    <p>
                      <span className="text-slate-500 inline-block w-20">Disputes:</span>
                      <strong className="text-slate-800">
                        {tenancy.disputes_count > 0 ? `${tenancy.disputes_count} (${tenancy.disputes_resolved || 0} resolved)` : 'None'}
                      </strong>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t-2 border-slate-800 pt-4 mt-auto">
              <div className="flex items-end justify-between">
                <div className="text-xs text-slate-500 max-w-md">
                  <p className="font-semibold text-slate-700 mb-1">DISCLAIMERS:</p>
                  <p>This report is based on verified landlord-submitted data. Tenants may dispute inaccurate entries under PIPEDA. Valid for 90 days from issue date.</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-3 justify-end mb-2">
                    <div className="w-16 h-16 bg-slate-100 border border-slate-300 rounded flex items-center justify-center">
                      <QrCode className="w-10 h-10 text-slate-400" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-600" style={{ fontFamily: 'Courier New, monospace' }}>
                    verify.tenantra.ca/{reportId}
                  </p>
                </div>
              </div>
              <div className="text-center mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-400">© {new Date().getFullYear()} Tenantra Inc. | PIPEDA Compliant | Canada</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: letter portrait; margin: 0.5in; }
        }
      `}</style>
    </div>
  );
}

export default OfficialRentReport;
